import React, { useState } from 'react';
import { Login } from './pages/Login';
import { Layout } from './components/Layout';
import { Dashboard } from './pages/Dashboard';
import { ProductList } from './pages/ProductList';
import { ProductForm } from './pages/ProductForm';
import { FeeSetup } from './pages/FeeSetup';
import { KYCGroupSetup } from './pages/KYCGroupSetup';
import { InterestSetup } from './pages/InterestSetup';
import { PenaltiesSetup } from './pages/PenaltiesSetup';
import { TaxSetup } from './pages/TaxSetup';
import { TaxesOnFeesSetup } from './pages/TaxesOnFeesSetup';
import { CreditScoringSetup } from './pages/CreditScoringSetup';
// Application Views
import { MasterView } from './pages/applications/MasterView';
import { ApplicationDetail } from './pages/applications/ApplicationDetail';
import { OnboardingMasterView } from './pages/applications/OnboardingMasterView';
import { OnboardingApplicationDetail } from './pages/applications/OnboardingApplicationDetail';
// Audit Views
import { LoanApplicationAuditLogs } from './pages/audit/LoanApplicationAuditLogs';
import { BackofficeAuditLogs } from './pages/audit/BackofficeAuditLogs';
export function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState<'maker' | 'checker'>('maker');
  const [userEmail, setUserEmail] = useState('');
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [selectedApplicationId, setSelectedApplicationId] = useState<string | null>(null);
  const [selectedOnboardingId, setSelectedOnboardingId] = useState<string | null>(null);
  const [applicationStatuses, setApplicationStatuses] = useState<Record<string, {
    status: string;
    logs: Array<{
      date: string;
      action: string;
      actionBy: string;
      comments: string;
    }>;
  }>>({});
  const [onboardingStatuses, setOnboardingStatuses] = useState<Record<string, {
    status: string;
    logs: Array<{
      date: string;
      action: string;
      actionBy: string;
      comments: string;
    }>;
  }>>({});
  // Navigation handler
  const handleNavigate = (page: string) => {
    setCurrentPage(page);
    // Reset states if navigating away
    if (page !== 'product-form') setSelectedTemplate(null);
    if (page !== 'app-detail') setSelectedApplicationId(null);
    if (page !== 'onboarding-detail') setSelectedOnboardingId(null);
  };
  // Product Form specific navigation
  const handleNavigateToForm = (templateId: string | null) => {
    setSelectedTemplate(templateId);
    setCurrentPage('product-form');
  };
  const handleBackToProductList = () => {
    setCurrentPage('loan-products');
    setSelectedTemplate(null);
  };
  // Application Detail navigation
  const handleNavigateToAppDetail = (appId: string) => {
    setSelectedApplicationId(appId);
    setCurrentPage('app-detail');
  };
  const handleBackToAppList = () => {
    setCurrentPage('app-master');
    setSelectedApplicationId(null);
  };
  // Onboarding Application navigation
  const handleNavigateToOnboardingDetail = (appId: string) => {
    setSelectedOnboardingId(appId);
    setCurrentPage('onboarding-detail');
  };
  const handleBackToOnboardingList = () => {
    setCurrentPage('onboarding-master');
    setSelectedOnboardingId(null);
  };
  // Handle application status changes
  const handleStatusChange = (appId: string, newStatus: string, comment: string, actionBy: string) => {
    const now = new Date();
    const timestamp = now.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
    setApplicationStatuses(prev => ({
      ...prev,
      [appId]: {
        status: newStatus,
        logs: [{
          date: timestamp,
          action: newStatus,
          actionBy: actionBy,
          comments: comment
        }, ...(prev[appId]?.logs || [])]
      }
    }));
  };
  // Handle onboarding status changes
  const handleOnboardingStatusChange = (appId: string, newStatus: string, comment: string, actionBy: string) => {
    const now = new Date();
    const timestamp = now.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
    setOnboardingStatuses(prev => ({
      ...prev,
      [appId]: {
        status: newStatus,
        logs: [{
          date: timestamp,
          action: newStatus,
          actionBy: actionBy,
          comments: comment
        }, ...(prev[appId]?.logs || [])]
      }
    }));
  };
  // Auth handlers
  const handleLogin = (role: 'maker' | 'checker', email: string) => {
    setIsAuthenticated(true);
    setUserRole(role);
    setUserEmail(email);
  };
  const handleLogout = () => {
    setIsAuthenticated(false);
    setCurrentPage('dashboard');
    setUserRole('maker');
    setUserEmail('');
  };
  // Render content based on current page
  const renderContent = () => {
    switch (currentPage) {
      case 'dashboard':
        return <Dashboard onNavigate={handleNavigate} data-id="element-0" />;
      // Application Management
      case 'app-master':
        return <MasterView onNavigateToDetail={handleNavigateToAppDetail} applicationStatuses={applicationStatuses} data-id="element-1" />;
      case 'app-detail':
        return selectedApplicationId ? <ApplicationDetail applicationId={selectedApplicationId} onBack={handleBackToAppList} userRole={userRole} userEmail={userEmail} onStatusChange={handleStatusChange} data-id="element-2" /> : <MasterView onNavigateToDetail={handleNavigateToAppDetail} applicationStatuses={applicationStatuses} data-id="element-3" />;
      // Onboarding Applications
      case 'onboarding-master':
        return <OnboardingMasterView onNavigateToDetail={handleNavigateToOnboardingDetail} applicationStatuses={onboardingStatuses} data-id="element-4" />;
      case 'onboarding-detail':
        return selectedOnboardingId ? <OnboardingApplicationDetail applicationId={selectedOnboardingId} onBack={handleBackToOnboardingList} userRole={userRole} userEmail={userEmail} onStatusChange={handleOnboardingStatusChange} data-id="element-5" /> : <OnboardingMasterView onNavigateToDetail={handleNavigateToOnboardingDetail} applicationStatuses={onboardingStatuses} data-id="element-6" />;
      // Product Management
      case 'loan-products':
        return <ProductList onNavigateToForm={handleNavigateToForm} data-id="element-7" />;
      case 'product-form':
        return <ProductForm onBack={handleBackToProductList} templateId={selectedTemplate} data-id="element-8" />;
      case 'fee-setup':
        return <FeeSetup data-id="element-9" />;
      case 'kyc-group':
        return <KYCGroupSetup data-id="element-10" />;
      case 'interest-setup':
        return <InterestSetup data-id="element-11" />;
      case 'penalties-setup':
        return <PenaltiesSetup data-id="element-12" />;
      case 'tax-setup':
        return <TaxSetup data-id="element-13" />;
      case 'taxes-on-fees':
        return <TaxesOnFeesSetup data-id="element-14" />;
      case 'credit-scoring':
        return <CreditScoringSetup data-id="element-15" />;
      // Audit Management
      case 'audit-loan-apps':
        return <LoanApplicationAuditLogs data-id="element-16" />;
      case 'audit-backoffice':
        return <BackofficeAuditLogs data-id="element-17" />;
      default:
        return <Dashboard onNavigate={handleNavigate} data-id="element-18" />;
    }
  };
  // Get page title for header
  const getPageTitle = () => {
    switch (currentPage) {
      case 'dashboard':
        return 'Dashboard';
      case 'app-master':
        return 'Loan Applications';
      case 'app-detail':
        return 'Loan Application Details';
      case 'onboarding-master':
        return 'Onboarding Applications';
      case 'onboarding-detail':
        return 'Onboarding Application Details';
      case 'loan-products':
        return 'Loan Product Setup';
      case 'product-form':
        return 'Product Configuration';
      case 'fee-setup':
        return 'Fee Setup';
      case 'kyc-group':
        return 'KYC Group Setup';
      case 'interest-setup':
        return 'Interest & Markup Setup';
      case 'penalties-setup':
        return 'Penalties Setup';
      case 'tax-setup':
        return 'Tax Setup';
      case 'taxes-on-fees':
        return 'Taxes on Fees';
      case 'credit-scoring':
        return 'Credit Scoring Rule Builder';
      case 'audit-loan-apps':
        return 'Loan Application Audit Logs';
      case 'audit-backoffice':
        return 'Backoffice Audit Logs';
      default:
        return 'Bunyaad Finance';
    }
  };
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} data-id="element-19" />;
  }
  return <Layout currentPage={currentPage.startsWith('app-') ? 'app-master' : currentPage.startsWith('onboarding-') ? 'onboarding-master' : currentPage} onNavigate={handleNavigate} onLogout={handleLogout} title={getPageTitle()} data-id="element-20">
      {renderContent()}
    </Layout>;
}